import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ContentCopyFilled from "@mui/icons-material/ContentCopy";
import Close from "@mui/icons-material/Close";
import { CircularProgress, IconButton } from "@mui/material";

import {
  MonthColumn,
  SendPanelCustomRow,
} from "../../../types/IFavoriteOrigins";
import { MONTHS_LOCAL } from "../../../types/IMonth";

import { ActionTrashIcon } from "../../../icons";
import { CustomCardHeader } from "../../../code-connect/component/CustomCardHeader";
import { CustomCard } from "../../../code-connect/component/CustomCard";
import { CustomButton } from "../../../code-connect/component/CustomButton";
import { CustomDataGridTable } from "../../../code-connect/component/CustomDataGridTable";
import { BUTTON_PRESETS } from "../../../code-connect/config/buttonConfigs";
import { ProductGuidancePanelProps } from "../../../types/IOriginManager";

const ProductGuidancePanel: React.FC<ProductGuidancePanelProps> = ({
  sendPanelOrigin,
  selectedProduct,
  selectedMonths,
  activeSplitMonths,
  getSplitMonthsForRow,
  getMonthLabel,
  getModeRows,
  formatPriceByShowAs,
  getDisplayPriceValue,
  getPriceShowAsValue,
  onClose,
  onCopyComplete,
  savedNotification,
  failedNotification,
  onSaveAdditionalTypes,
}) => {
  const [sendPanelCustomRows, setSendPanelCustomRows] = useState<
    SendPanelCustomRow[]
  >([]);
  const [sendPanelSelectedCustomRowId, setSendPanelSelectedCustomRowId] =
    useState<string | null>(null);
  const [isCopyInProgress, setIsCopyInProgress] = useState(false);
  const [hasCustomRowStructureChanges, setHasCustomRowStructureChanges] =
    useState(false);
  const sendPanelCustomRowCounterRef = useRef(0);

  const sendPanelMonthColumns = useMemo<MonthColumn[]>(() => {
    const rowSplitMonths = new Set(getSplitMonthsForRow(sendPanelOrigin));
    return selectedMonths.reduce<MonthColumn[]>((acc, month) => {
      const monthIsSplitInHeader = activeSplitMonths.includes(month);
      const monthIsSplitForRow = rowSplitMonths.has(month);

      if (monthIsSplitInHeader && monthIsSplitForRow) {
        acc.push(
          { month, half: "h1", isSplit: true },
          { month, half: "h2", isSplit: true },
        );
        return acc;
      }

      acc.push({ month, half: "full", isSplit: false });
      return acc;
    }, []);
  }, [activeSplitMonths, getSplitMonthsForRow, selectedMonths, sendPanelOrigin]);

  const sendPanelColumns = useMemo(() => {
    const getSubHeaderName = (column: MonthColumn) => {
      if (!column.isSplit) {
        return "";
      }
      return column.half === "h1" ? "1H" : "2H";
    };

    const monthTableColumns = sendPanelMonthColumns.map((column, index) => ({
      field: `month-${index}`,
      headerName:
        !column.isSplit || column.half === "h1"
          ? getMonthLabel(column.month)
          : "",
      subHeaderName: getSubHeaderName(column),
      align: "center" as const,
    }));

    return [
      {
        field: "priceType",
        headerName: "Price Type",
        subHeaderName: "",
        align: "left" as const,
      },
      ...monthTableColumns,
      {
        field: "actions",
        headerName: <span className="dom-send-panel-actions-header-spacer" />,
        subHeaderName: "",
        align: "center" as const,
      },
    ];
  }, [getMonthLabel, sendPanelMonthColumns]);

  const sendPanelMonthFieldKeys = useMemo(
    () =>
      sendPanelColumns
        .map((column) => column.field)
        .filter((field) => field.startsWith("month-")),
    [sendPanelColumns],
  );
  const sendPanelMonthFieldSignature = sendPanelMonthFieldKeys.join("|");

  const sendPanelMonthColumnsRef = useRef(sendPanelMonthColumns);
  sendPanelMonthColumnsRef.current = sendPanelMonthColumns;

  useEffect(() => {
    const columns = sendPanelMonthColumnsRef.current;
    const nameOrderMap = new Map<string, number>();

    sendPanelOrigin.months.forEach((monthEntry) => {
      monthEntry.periods.forEach((period) => {
        const infos = period.prices.additionalTypes ?? [];
        if (infos.length === 0) {
          return;
        }

        infos
          .slice()
          .sort(
            (a, b) =>
              (a.order ?? Number.MAX_SAFE_INTEGER) -
              (b.order ?? Number.MAX_SAFE_INTEGER),
          )
          .forEach((at) => {
            const trimmedName = at.name.trim();
            if (!trimmedName) {
              return;
            }

            const nextOrder = at.order ?? Number.MAX_SAFE_INTEGER;
            const currentOrder = nameOrderMap.get(trimmedName);

            if (currentOrder === undefined || nextOrder < currentOrder) {
              nameOrderMap.set(trimmedName, nextOrder);
            }
          });
      });
    });

    const allNames = [...nameOrderMap.entries()]
      .sort((a, b) => {
        const orderDelta = a[1] - b[1];
        if (orderDelta !== 0) {
          return orderDelta;
        }
        return a[0].localeCompare(b[0]);
      })
      .map(([name]) => name);

    if (allNames.length === 0) {
      setSendPanelCustomRows([]);
      setSendPanelSelectedCustomRowId(null);
      sendPanelCustomRowCounterRef.current = 0;
      return;
    }

    let counter = 0;
    const initialRows: SendPanelCustomRow[] = allNames.map((name) => {
      counter += 1;
      const rowId = `custom-row-${counter}`;
      const values: Record<string, string> = {};

      columns.forEach((column, index) => {
        const monthNum = MONTHS_LOCAL.indexOf(column.month) + 1;
        const monthEntry = sendPanelOrigin.months.find(
          (m) => m.month.month === monthNum,
        );

        let inputValue = "";
        if (monthEntry) {
          const periodIndex = column.half === "h2" ? 1 : 0;
          const period = monthEntry.periods[periodIndex];
          if (period) {
            const at = (period.prices.additionalTypes ?? []).find(
              (a) => a.name.trim() === name,
            );
            inputValue = at?.value ?? (at?.price != null ? String(at.price) : "");
          }
        }

        values[`month-${index}`] = inputValue;
      });

      return { id: rowId, priceType: name, values };
    });

    sendPanelCustomRowCounterRef.current = counter;
    setSendPanelCustomRows(initialRows);
    setSendPanelSelectedCustomRowId(null);
  }, [sendPanelOrigin]);

  useEffect(() => {
    setSendPanelCustomRows((prev) =>
      prev.map((row) => {
        const nextValues = sendPanelMonthFieldKeys.reduce<Record<string, string>>(
          (acc, field) => {
            acc[field] = row.values[field] ?? "";
            return acc;
          },
          {},
        );
        return {
          ...row,
          values: nextValues,
        };
      }),
    );
  }, [sendPanelMonthFieldKeys, sendPanelMonthFieldSignature]);

  const handleSendPanelCustomCellChange = useCallback(
    (rowId: string, field: string, value: string) => {
      setSendPanelCustomRows((prev) =>
        prev.map((row) => {
          if (row.id !== rowId) {
            return row;
          }

          if (field === "priceType") {
            return {
              ...row,
              priceType: value,
            };
          }

          return {
            ...row,
            values: {
              ...row.values,
              [field]: value,
            },
          };
        }),
      );
    },
    [],
  );

  const handleAddSendPanelRow = useCallback(() => {
    const nextValues = sendPanelMonthFieldKeys.reduce<Record<string, string>>(
      (acc, field) => {
        acc[field] = "";
        return acc;
      },
      {},
    );

    sendPanelCustomRowCounterRef.current += 1;
    const nextRowId = `custom-row-${sendPanelCustomRowCounterRef.current}`;

    setSendPanelCustomRows((prev) => [
      ...prev,
      {
        id: nextRowId,
        priceType: "",
        values: nextValues,
      },
    ]);
    setHasCustomRowStructureChanges(true);
  }, [sendPanelMonthFieldKeys]);

  const handleDeleteSendPanelRow = useCallback((rowId: string) => {
    setSendPanelCustomRows((prev) => prev.filter((row) => row.id !== rowId));
    setSendPanelSelectedCustomRowId((prev) => (prev === rowId ? null : prev));
    setHasCustomRowStructureChanges(true);
  }, []);

  const handleCloseSendPanel = useCallback(async () => {
    if (isCopyInProgress) {
      return;
    }

    if (!hasCustomRowStructureChanges) {
      onClose();
      return;
    }

    setIsCopyInProgress(true);
    try {
      await onSaveAdditionalTypes(sendPanelCustomRows, sendPanelMonthColumns);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      failedNotification("Failed to save", message);
    } finally {
      setIsCopyInProgress(false);
    }
  }, [
    failedNotification,
    hasCustomRowStructureChanges,
    isCopyInProgress,
    onClose,
    onSaveAdditionalTypes,
    sendPanelCustomRows,
    sendPanelMonthColumns,
  ]);

  const sendPanelRows = useMemo(() => {
    const baseRows = getModeRows(sendPanelOrigin).map((modeRow) => {
      let sendPanelPriceTypeLabel = modeRow.label;
      if (modeRow.key === "truck") {
        sendPanelPriceTypeLabel = "TRUCK ALT";
      } else if (modeRow.key === "rail") {
        sendPanelPriceTypeLabel = "RAIL ALT";
      }

      const nextRow: Record<string, React.ReactNode> = {
        id: modeRow.key,
        priceType: sendPanelPriceTypeLabel,
        actions: "",
      };

      sendPanelMonthColumns.forEach((column, index) => {
        const monthHalfModeRow =
          getModeRows(sendPanelOrigin, column.month, column.half).find(
            (row) => row.key === modeRow.key,
          ) ?? modeRow;

        nextRow[`month-${index}`] = formatPriceByShowAs(
          getDisplayPriceValue(
            sendPanelOrigin,
            monthHalfModeRow,
            column.month,
            column.half,
          ),
          getPriceShowAsValue(
            sendPanelOrigin,
            monthHalfModeRow,
            column.month,
            column.half,
          ),
        );
      });

      return nextRow;
    });

    const customRows = sendPanelCustomRows.map((customRow) => {
      const isCustomRowSelected = sendPanelSelectedCustomRowId === customRow.id;

      const nextRow: Record<string, React.ReactNode> = {
        id: customRow.id,
        priceType: (
          <div
            className={`dom-send-panel-custom-row-price-type${
              isCustomRowSelected
                ? " dom-send-panel-custom-row-price-type--selected"
                : ""
            }`}
          >
            <input
              type="text"
              className="dom-send-panel-cell-input dom-send-panel-cell-input--price-type"
              value={customRow.priceType}
              onFocus={() => setSendPanelSelectedCustomRowId(customRow.id)}
              onMouseDown={() => setSendPanelSelectedCustomRowId(customRow.id)}
              onChange={(event) =>
                handleSendPanelCustomCellChange(
                  customRow.id,
                  "priceType",
                  event.target.value,
                )
              }
              placeholder="Type"
              aria-label="Type"
            />
          </div>
        ),
        actions: (
          <button
            id={`delete-custom-row-btn-${customRow.id}`}
            type="button"
            className="dom-cell action-btn group-cell dom-delete-btn"
            aria-label="Delete"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleDeleteSendPanelRow(customRow.id);
            }}
          >
            <ActionTrashIcon />
          </button>
        ),
      };

      sendPanelMonthColumns.forEach((column, index) => {
        const field = `month-${index}`;
        const monthColumn = sendPanelColumns[index + 1] as
          | { headerName?: string; subHeaderName?: string }
          | undefined;
        const monthHeader = monthColumn?.headerName?.trim() ?? "";
        const monthSubHeader = monthColumn?.subHeaderName?.trim() ?? "";
        const monthPlaceholder = monthSubHeader
          ? `${column.month} ${monthSubHeader}`
          : monthHeader;
        nextRow[field] = (
          <input
            id={`dom-send-panel-cell-input-${customRow.id}-${field}`}
            type="text"
            inputMode="decimal"
            className="dom-send-panel-cell-input"
            value={customRow.values[field] ?? ""}
            onFocus={() => setSendPanelSelectedCustomRowId(customRow.id)}
            onMouseDown={() => setSendPanelSelectedCustomRowId(customRow.id)}
            onChange={(event) =>
              handleSendPanelCustomCellChange(
                customRow.id,
                field,
                event.target.value,
              )
            }
            placeholder={monthPlaceholder || "Price"}
            aria-label={field}
          />
        );
      });

      return nextRow;
    });

    return [...baseRows, ...customRows];
  }, [
    formatPriceByShowAs,
    getDisplayPriceValue,
    getModeRows,
    getPriceShowAsValue,
    handleDeleteSendPanelRow,
    handleSendPanelCustomCellChange,
    sendPanelColumns,
    sendPanelCustomRows,
    sendPanelMonthColumns,
    sendPanelOrigin,
    sendPanelSelectedCustomRowId,
  ]);

  const handleCopySendPanelToClipboard = useCallback(async () => {
    if (isCopyInProgress) {
      return;
    }

    setIsCopyInProgress(true);

    try {
      await onSaveAdditionalTypes(sendPanelCustomRows, sendPanelMonthColumns);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      failedNotification("Failed to save", message);
      setIsCopyInProgress(false);
    }

    onCopyComplete();

    if (!window.isSecureContext || typeof ClipboardItem === "undefined") {
      failedNotification(
        "Failed to copy",
        "Copy requires a secure browser context.",
      );
      return;
    }

    try {
      const monthCols = sendPanelColumns.filter((col) => col.field.startsWith("month-"));
      const hasAnySubHeader = monthCols.some(
        (col) => typeof col.subHeaderName === "string" && col.subHeaderName.trim().length > 0,
      );

      const normalizeLabel = (label: string): string => {
        const map: Record<string, string> = {
          "Posted Px": "Posted Price",
          "TRUCK ALT": "Truck ALT",
          "RAIL ALT": "Rail ALT",
        };
        return map[label.trim()] ?? label;
      };

      const headerGroups: Array<{ key: string; header: string; colSpan: number }> = [];
      monthCols.forEach((col) => {
        const header = typeof col.headerName === "string" ? col.headerName.trim() : "";
        if (!header && headerGroups.length > 0) {
          headerGroups[headerGroups.length - 1].colSpan += 1;
        } else {
          headerGroups.push({ key: col.field, header, colSpan: 1 });
        }
      });

      const baseRowsData = getModeRows(sendPanelOrigin).map((modeRow) => {
        let priceTypeLabel = modeRow.label;
        if (modeRow.key === "truck") priceTypeLabel = "TRUCK ALT";
        else if (modeRow.key === "rail") priceTypeLabel = "RAIL ALT";
        priceTypeLabel = normalizeLabel(priceTypeLabel);

        const cells = monthCols.map((col) => {
          const colIndex = parseInt(col.field.split("-")[1], 10);
          const column = sendPanelMonthColumns[colIndex];
          const monthHalfModeRow =
            getModeRows(sendPanelOrigin, column.month, column.half).find(
              (row) => row.key === modeRow.key,
            ) ?? modeRow;
          return formatPriceByShowAs(
            getDisplayPriceValue(sendPanelOrigin, monthHalfModeRow, column.month, column.half),
            getPriceShowAsValue(sendPanelOrigin, monthHalfModeRow, column.month, column.half),
          );
        });
        return { label: priceTypeLabel, cells };
      });

      const customRowsData = sendPanelCustomRows.map((customRow) => {
        const label = normalizeLabel(customRow.priceType);
        const cells = monthCols.map((col) => customRow.values[col.field] ?? "");
        return { label, cells };
      });

      const allRows = [...baseRowsData, ...customRowsData];

      const cardTitle = sendPanelOrigin.location.name.toUpperCase();
      const cardSubheader = `${selectedProduct} ${sendPanelOrigin.competitor ? "Competitor Indications" : "Guidance"}`;
      const shouldShowGuidanceNote =
        cardSubheader === `${selectedProduct} Guidance`;
      const pasteSubheader = shouldShowGuidanceNote
        ? `${cardSubheader}:`
        : cardSubheader;
      const guidanceNote =
        "Please bring opportunities below guidance for consideration.";

      const totalWidth = 3200;
      const baseColWidth = Math.floor(totalWidth * 0.32);
      const remainingWidth = totalWidth - baseColWidth;
      const baseMonthColWidth = Math.floor(remainingWidth / monthCols.length);

      const colWidths = [baseColWidth, ...monthCols.map(() => baseMonthColWidth)];

      const rowHeight = 150;
      const headerRowsCount = hasAnySubHeader ? 2 : 1;
      const cardHeaderSectionHeight = 240;
      const totalHeight = cardHeaderSectionHeight + (headerRowsCount + allRows.length) * rowHeight;

      const scale = 2.5;

      const canvas = document.createElement("canvas");
      canvas.width = totalWidth * scale;
      canvas.height = totalHeight * scale;
      const ctx = canvas.getContext("2d", { alpha: false });

      if (!ctx) {
        throw new Error("Could not create canvas context");
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.scale(scale, scale);

      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, totalWidth, totalHeight);

      ctx.fillStyle = "#334155";
      ctx.font = "bold 72px Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(cardTitle, 72, cardHeaderSectionHeight * 0.38);

      ctx.fillStyle = "#64748B";
      ctx.font = "52px Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";
      ctx.fillText(pasteSubheader, 72, cardHeaderSectionHeight * 0.72);

      if (shouldShowGuidanceNote) {
        const subheaderWidth = ctx.measureText(pasteSubheader).width;
        ctx.fillStyle = "#64748B";
        ctx.font = "52px Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";
        ctx.fillText(guidanceNote, 72 + subheaderWidth + 40, cardHeaderSectionHeight * 0.72);
      }

      ctx.fillStyle = "rgba(144, 161, 185, 0.12)";
      ctx.fillRect(0, cardHeaderSectionHeight, totalWidth, rowHeight * headerRowsCount);

      ctx.fillStyle = "#1E293B";
      ctx.font = "bold 60px Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";

      ctx.fillText("Price Type", 72, cardHeaderSectionHeight + (rowHeight * headerRowsCount) / 2);

      ctx.textAlign = "center";
      let currentX = colWidths[0];
      headerGroups.forEach((group) => {
        const groupWidth = group.colSpan * baseMonthColWidth;
        ctx.fillText(group.header, currentX + groupWidth / 2, cardHeaderSectionHeight + rowHeight / 2);
        currentX += groupWidth;
      });

      if (hasAnySubHeader) {
        let subX = colWidths[0];
        monthCols.forEach((col) => {
          const subText = typeof col.subHeaderName === "string" ? col.subHeaderName : "";
          ctx.fillText(subText, subX + baseMonthColWidth / 2, cardHeaderSectionHeight + rowHeight + rowHeight / 2);
          subX += baseMonthColWidth;
        });
      }

      ctx.font = "56px Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif";
      allRows.forEach((row, rowIndex) => {
        const y = cardHeaderSectionHeight + (headerRowsCount + rowIndex) * rowHeight;

        ctx.strokeStyle = "#CBD5E1";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(0, y + rowHeight);
        ctx.lineTo(totalWidth, y + rowHeight);
        ctx.stroke();

        ctx.fillStyle = "#334155";
        ctx.textAlign = "left";
        ctx.fillText(row.label, 72, y + rowHeight / 2);

        ctx.textAlign = "center";
        ctx.fillStyle = "#0F172A";
        row.cells.forEach((cellValue, cellIndex) => {
          const cellX = colWidths[0] + cellIndex * baseMonthColWidth;
          ctx.fillText(cellValue, cellX + baseMonthColWidth / 2, y + rowHeight / 2);
        });
      });

      canvas.toBlob(async (blob) => {
        if (!blob) {
          failedNotification("Failed to copy", "Canvas blob creation failed.");
          return;
        }
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": blob }),
        ]);
        savedNotification("Table copied to clipboard as an image");
      }, "image/png");

    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      failedNotification("Failed to copy", message);
      setIsCopyInProgress(false);
    }
  }, [
    failedNotification,
    formatPriceByShowAs,
    getDisplayPriceValue,
    getModeRows,
    getPriceShowAsValue,
    isCopyInProgress,
    onCopyComplete,
    onSaveAdditionalTypes,
    savedNotification,
    sendPanelColumns,
    sendPanelCustomRows,
    sendPanelMonthColumns,
    sendPanelOrigin,
    selectedProduct,
  ]);

  const sendPanelCardHeaderProps = useMemo(
    () => ({
      title: sendPanelOrigin.location.name.toUpperCase(),
      subheader: `${selectedProduct} ${
        sendPanelOrigin.competitor ? "Competitor Indications" : "Guidance"
      }`,
      subheaderTypographyProps: {
        style: {
          textTransform: "none",
        },
      },
      titleColor: "#334155",
      titleFontSize: "24px",
      subheaderColor: "#64748B",
      subheaderFontSize: "14px",
      className: "dom-send-panel-card-header",
      action: (
        <IconButton aria-label="Close" size="medium" onClick={handleCloseSendPanel}>
          <Close fontSize="medium" />
        </IconButton>
      ),
    }),
    [handleCloseSendPanel, selectedProduct, sendPanelOrigin],
  );

  return (
    <div className="dom-send-panel-root">
      {isCopyInProgress && (
        <div className="dom-send-panel-loader-overlay" role="status" aria-live="polite">
          <div className="dom-send-panel-loader-card">
            <CircularProgress size={34} thickness={4.5} />
            <span>Saving and copying...</span>
          </div>
        </div>
      )}
      <CustomCard>
        <CustomCardHeader {...sendPanelCardHeaderProps} />
        <div className="custom-card-data-grid-table-wrapper">
          <CustomDataGridTable
            rows={sendPanelRows}
            columns={sendPanelColumns}
            tableFooter={false}
            className="dom-send-panel-data-grid"
          />
          <div
            id="dom-add-row-button"
            className={`dom-send-panel-add-row-wrap${
              sendPanelCustomRows.length > 0
                ? " dom-send-panel-add-row-wrap--with-top-border"
                : ""
            }`}
          >
            <CustomButton
              {...BUTTON_PRESETS.addRow}
              type="button"
              className="dom-send-panel-add-row-btn"
              onClick={handleAddSendPanelRow}
            />
          </div>
          <p className="dom-send-panel-guidance-note">
            Please bring opportunities below guidance for consideration.
          </p>
        </div>
        <div className="dom-copy-to-clipboard-row">
          <CustomButton
            {...BUTTON_PRESETS.copyToClipboard}
            className="dom-copy-to-clipboard-btn"
            startIcon={<ContentCopyFilled />}
            onClick={handleCopySendPanelToClipboard}
          />
        </div>
      </CustomCard>
    </div>
  );
};

export default ProductGuidancePanel;
